import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { buildMenu } from "./Sidebar";
import { SETTINGS_SEARCH_ITEMS } from "@/pages/SettingsPage";
import { search } from "@/api/search";
import { StatusPill } from "@/components/StatusPill";
import { formatDatum } from "@/lib/format";

type Props = {
  open: boolean;
  onClose: () => void;
  posteingangAktiv?: boolean;
};

type Group = "Seiten" | "Einstellungen" | "Buchungen" | "Anfragen";

type Entry = {
  group: Group;
  label: string;
  hint: string;
  icon: string;
  action: () => void;
  meta?: ReactNode;
};

const GROUP_ORDER: Group[] = ["Buchungen", "Anfragen", "Einstellungen", "Seiten"];

export function CommandPalette({ open, onClose, posteingangAktiv = false }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");

  // Tipp-Verzögerung für die serverseitige Suche (statische Treffer bleiben sofort).
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [q]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setDebounced("");
    }
  }, [open]);

  // Statische Quellen: Seiten + Einstellungs-Bereiche (lokal gefiltert).
  const staticEntries: Entry[] = useMemo(() => {
    const pages: Entry[] = buildMenu(posteingangAktiv).map((m) => ({
      group: "Seiten",
      label: m.label,
      hint: m.external ? "Externer Link" : "Seite öffnen",
      icon: m.icon,
      action: () => (m.external ? window.location.assign(m.path) : navigate(m.path)),
    }));
    const settings: Entry[] = SETTINGS_SEARCH_ITEMS.map((s) => ({
      group: "Einstellungen",
      label: s.label,
      hint: `Einstellungen · ${s.group}`,
      icon: s.icon,
      action: () => navigate(s.path),
    }));
    return [...pages, ...settings];
  }, [navigate, posteingangAktiv]);

  const filteredStatic = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return staticEntries;
    return staticEntries.filter((e) => e.label.toLowerCase().includes(needle) || e.hint.toLowerCase().includes(needle));
  }, [staticEntries, q]);

  // Dynamische Quelle: Buchungen + Anfragen vom Backend (ab 2 Zeichen).
  const searchQuery = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => search(debounced),
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });

  const dynamicEntries: Entry[] = useMemo(() => {
    const data = searchQuery.data;
    if (!data) return [];
    const bookings: Entry[] = data.bookings.map((b) => ({
      group: "Buchungen",
      label: `${b.buchungsreferenz ? `${b.buchungsreferenz} · ` : ""}${b.gruppenname || b.gastname || "Buchung"}`,
      hint: [b.gastname, b.check_in ? `${formatDatum(b.check_in)}–${formatDatum(b.check_out)}` : "", b.einheiten_namen.join(", ")]
        .filter(Boolean)
        .join(" · "),
      icon: "event_note",
      action: () => navigate(`/bookings/${b.id}`),
      meta: b.status ? <StatusPill status={b.status} /> : undefined,
    }));
    const inquiries: Entry[] = data.inquiries.map((i) => ({
      group: "Anfragen",
      label: i.gruppenname || i.kontakt || "Anfrage",
      hint: [i.kontakt, i.email, i.check_in ? `${formatDatum(i.check_in)}–${formatDatum(i.check_out)}` : ""]
        .filter(Boolean)
        .join(" · "),
      icon: "mark_email_unread",
      action: () => navigate("/inquiries"),
    }));
    return [...bookings, ...inquiries];
  }, [searchQuery.data, navigate]);

  const grouped = useMemo(() => {
    const all = [...dynamicEntries, ...filteredStatic];
    const map = new Map<Group, Entry[]>();
    for (const e of all) {
      const list = map.get(e.group) ?? [];
      list.push(e);
      map.set(e.group, list);
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ group: g, entries: map.get(g)! }));
  }, [dynamicEntries, filteredStatic]);

  const isSearching = open && debounced.length >= 2 && searchQuery.isFetching;
  const hasResults = grouped.length > 0;

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(24,20,16,.35)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "10vh",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 92vw)",
          background: "var(--bg-card)",
          borderRadius: 14,
          boxShadow: "var(--sh-3)",
          overflow: "hidden",
          border: "1px solid var(--border)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
          <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--fg-muted)" }}>
            search
          </span>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Seite, Buchung, Anfrage oder Einstellung suchen…"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              color: "var(--fg)",
              fontFamily: "var(--font-body)",
              fontSize: 14,
            }}
          />
          <kbd
            style={{
              fontSize: 10,
              fontFamily: "var(--font-mono)",
              background: "var(--bg-subtle)",
              border: "1px solid var(--border)",
              padding: "1px 6px",
              borderRadius: 4,
              color: "var(--fg-muted)",
            }}
          >
            Esc
          </kbd>
        </div>
        <div style={{ maxHeight: 360, overflowY: "auto" }}>
          {!hasResults ? (
            <div style={{ padding: "18px 16px", color: "var(--fg-muted)", fontSize: 13 }}>
              {isSearching ? "Suche läuft…" : "Keine Treffer."}
            </div>
          ) : (
            grouped.map(({ group, entries }) => (
              <div key={group}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: ".09em",
                    color: "var(--fg-subtle)",
                    fontWeight: 600,
                    padding: "10px 16px 4px",
                  }}
                >
                  <span>{group}</span>
                  {group === "Buchungen" && isSearching ? <span style={{ textTransform: "none" }}>…</span> : null}
                </div>
                {entries.map((e, i) => (
                  <button
                    key={`${group}-${i}`}
                    onClick={() => {
                      e.action();
                      onClose();
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      padding: "9px 16px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "var(--font-body)",
                      color: "var(--fg)",
                    }}
                    onMouseEnter={(ev) => (ev.currentTarget.style.background = "var(--bg-hover)")}
                    onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}
                  >
                    <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--fg-muted)", flexShrink: 0 }}>
                      {e.icon}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {e.label}
                      </div>
                      {e.hint ? (
                        <div style={{ fontSize: 11, color: "var(--fg-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {e.hint}
                        </div>
                      ) : null}
                    </div>
                    {e.meta ? <span style={{ flexShrink: 0 }}>{e.meta}</span> : null}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
